import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Card, Tag, Space, Typography, Divider, Button } from "antd";
import { PrinterOutlined, ClockCircleOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import Layout from "../../ReusableComponents/Layout";
import Letter from "../Letter";
import getSingleUser from "../../Redux/APIs/usersAPIs/getSingleUser";
import deleteUser from "../../Redux/APIs/usersAPIs/delete";
import IntructionLetter from "../IntructionLetter";
import { Icons } from "../../Utils/icons-utils";
import EditUserDetailsModal from "../../ReusableComponents/EditUserDetailsModal/EditUserDetailsModal";
import { format, parseISO } from "date-fns";
import DetailsCard from "./components/DetailsCard";
import AddresCard from "./components/AddresCard";
import MoveDateCard from "./components/MoveDateCard";
import { getCompanyStatus } from "../../Utils/statusUtils";
import { formatAddress } from "../GuestDetails/utils";
import useLetterSettingsQuery from "../../api/queries/useLetterSettingsQuery";

const { Title, Text } = Typography;

const UserDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { data: letterSettings } = useLetterSettingsQuery();

  useEffect(() => {
    dispatch(getSingleUser(id, setData));
  }, []);

  const reloadData = () => {
    dispatch(getSingleUser(id, setData));
  };

  return (
    <Layout>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16, padding: 0, fontSize: 14 }}
      >
        Back
      </Button>
      <EditUserDetailsModal
        isOpen={editModalOpen}
        setIsOpen={setEditModalOpen}
        data={data}
        reloadData={reloadData}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={6}>
          <DetailsCard data={data} setEditModalOpen={setEditModalOpen} />
        </Col>
        <Col xs={24} md={12}>
          <AddresCard data={data} />
        </Col>
        <Col xs={24} md={6}>
          <MoveDateCard
            data={data}
            getCompanyStatus={getCompanyStatus}
            deleteUser={(id) => dispatch(deleteUser(id))}
          />
        </Col>
      </Row>

      {data?.moveinInfo?.map((moveInfo, index) => (
        <React.Fragment key={index}>
          {data?.moveinInfo?.length > 1 && index !== 0 && (
            <Card style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={16}>
                  <Title level={5}>Moveinout Details</Title>
                  <Text strong>New address: </Text>
                  <Text>{formatAddress(moveInfo?.newAddress)}</Text>
                  <br />
                  <Text strong>Old address: </Text>
                  <Text>{formatAddress(moveInfo?.oldAddress)}</Text>
                  <br />
                  <Text strong>Total Companies: </Text>
                  <Text>{moveInfo?.companies?.length}</Text>
                </Col>
                <Col span={8}>
                  <Title level={5}>Move Date</Title>
                  <Title level={2} style={{ margin: 0 }}>
                    {moveInfo?.movingDate ? format(parseISO(moveInfo.movingDate), "do") : "--"}
                  </Title>
                  <Text type="secondary">
                    {moveInfo?.movingDate ? format(parseISO(moveInfo.movingDate), "MMM yyyy") : "--"}
                  </Text>
                </Col>
              </Row>
            </Card>
          )}

          <div>
            {moveInfo?.companies?.map((company) => (
              <Card
                key={company._id}
                size="small"
                style={{ marginBottom: 12 }}
              >
                <Row align="middle" gutter={16}>
                  <Col>
                    <img
                      src={Icons[company?.company?.[0]?.service?.[0]?.name]}
                      alt={company?.company?.[0]?.service?.[0]?.name}
                      style={{ width: 40, height: 40, objectFit: "contain" }}
                    />
                  </Col>
                  <Col flex="auto">
                    <Text strong>{company?.company?.[0]?.name}</Text>
                    <br />
                    {company?.printed ? (
                      <Tag color="green">Printed</Tag>
                    ) : (
                      <Tag color="orange">To be printed</Tag>
                    )}
                  </Col>
                  {company.completed ? (
                    <Col>
                      <Space>
                        <Letter
                          button={
                            <button style={{ border: "none", background: "transparent", cursor: "pointer" }}>
                              <PrinterOutlined style={{ fontSize: 20, color: "#00AFEE" }} />
                            </button>
                          }
                          id={company?._id}
                          name={`${data?.firstName} ${data?.lastName}`}
                          oldAddress={moveInfo?.oldAddress?.summaryline}
                          newAddress={moveInfo?.newAddress?.summaryline}
                          companyName={company?.company?.[0]?.name}
                          companyAddress={company?.company?.[0]?.address}
                          movingDate={moveInfo?.movingDate}
                          dob={data?.dateOfBirth}
                          email={data?.email}
                          phone={data?.phone}
                          serviceName={company?.company?.[0]?.service?.[0]?.name}
                          required={company?.required}
                          requiredDetails={company?.company?.[0]?.required}
                          requiredAPI={company?.required}
                          printSettings={letterSettings}
                        />
                        <IntructionLetter
                          id={company?._id}
                          firstName={data?.firstName}
                          lastName={data?.lastName}
                          name={`${data?.firstName} ${data?.lastName}`}
                          oldAddress={moveInfo?.oldAddress?.summaryline}
                          newAddress={moveInfo?.newAddress?.summaryline}
                          companyName={company?.company?.[0]?.name}
                          companyAddress={company?.company?.[0]?.address}
                          movingDate={moveInfo?.movingDate}
                          dob={data?.dateOfBirth}
                          email={data?.email}
                          phone={data?.phone}
                          serviceName={company?.company?.[0]?.service?.[0]?.name}
                          required={company?.required}
                          requiredDetails={company?.company?.[0]?.required}
                          requiredAPI={company?.required}
                        />
                      </Space>
                    </Col>
                  ) : (
                    <Col>
                      <Tag icon={<ClockCircleOutlined />} color="processing">In Progress</Tag>
                    </Col>
                  )}
                </Row>
                {company.completed && company?.required && (
                  <>
                    <Divider style={{ margin: "12px 0" }} />
                    <Space wrap>
                      {Object.entries(company.required).map(([key, value]) => (
                        <div key={key}>
                          <Text type="secondary" style={{ fontSize: 12 }}>{key}</Text>
                          <br />
                          <Text>{value}</Text>
                        </div>
                      ))}
                    </Space>
                  </>
                )}
              </Card>
            ))}
          </div>
        </React.Fragment>
      ))}
    </Layout>
  );
};

export default UserDetails;
